import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

export type ItemType = 'animal' | 'fertilizer' | 'nutrition' | 'equipment' | 'feed' | 'medicine' | 'other';

export type LotStatus = 'for_sale' | 'sold' | 'reserved' | 'expired';

export interface LotItem {
  id: string;
  identifier: string; // Animal tag number, product code, etc.
  description?: string;
  weight?: number;
  age?: number; // For animals
  breed?: string; // For animals
  condition?: string; // New, used, etc.
}

export interface SalesLot {
  id: string;
  organizationId: string;
  organizationName: string;
  title: string;
  itemType: ItemType;
  items: LotItem[];
  pricePerItem: number;
  currency: string;
  totalValue: number;
  status: LotStatus;
  description?: string;
  location?: string;
  contactInfo?: {
    phone?: string;
    email?: string;
    preferredContact?: 'phone' | 'email' | 'chat';
  };
  createdAt: string;
  updatedAt: string;
  expiresAt?: string;
  viewCount: number;
  isUrgent?: boolean;
  tags: string[];
}

export interface MarketplaceSearchFilters {
  itemType?: ItemType;
  organizationId?: string;
  minPrice?: number;
  maxPrice?: number;
  location?: string;
  status?: LotStatus;
  tags?: string[];
}

export interface MarketplaceSearchResult {
  lots: SalesLot[];
  totalCount: number;
  filters: MarketplaceSearchFilters;
}

export interface CreateLotRequest {
  organizationId: string;
  itemType: ItemType;
  items: Omit<LotItem, 'id'>[];
  pricePerItem: number;
  currency?: string;
  title?: string;
  description?: string;
  location?: string;
  contactInfo?: {
    phone?: string;
    email?: string;
    preferredContact?: 'phone' | 'email' | 'chat';
  };
  expiresAt?: string;
  isUrgent?: boolean;
  tags?: string[];
}

export interface MarketplaceIntent {
  action: 'create_lot' | 'search_lots' | 'view_lot' | 'update_lot' | 'delete_lot';
  itemType?: ItemType;
  items?: string[]; // Item identifiers from user input
  pricePerItem?: number;
  currency?: string;
  organizationId?: string;
  lotId?: string;
  searchQuery?: string;
  filters?: MarketplaceSearchFilters;
}

// Labels for display
export const itemTypeLabels: Record<ItemType, string> = {
  animal: 'Animais',
  fertilizer: 'Fertilizantes',
  nutrition: 'Nutrição Animal',
  equipment: 'Equipamentos',
  feed: 'Ração',
  medicine: 'Medicamentos',
  other: 'Outros'
};

export const lotStatusLabels: Record<LotStatus, string> = {
  for_sale: 'À Venda',
  sold: 'Vendido',
  reserved: 'Reservado',
  expired: 'Expirado'
};

export const lotStatusColors: Record<LotStatus, string> = {
  for_sale: DESIGN_TOKENS.colors.status.success[500], // Green
  sold: DESIGN_TOKENS.colors.neutral[500], // Gray
  reserved: DESIGN_TOKENS.colors.status.warning[500], // Amber
  expired: DESIGN_TOKENS.colors.status.error[500] // Red
};